import * as React from "react";

import {
  ChartForm as ChartFormType,
  ChartRouteBaseParams,
  ChartRow,
  ChartRowElement,
  ChartRowElementImportedProps,
  ChartSectionHeader,
  ChartSubsection,
  ChartSubsectionContent,
  ChartSubsectionHeader,
  mapFormToPayload,
  mapPayloadToFormObject,
  useChartApplication,
  useSaveAndNext,
  useUpdateChartApplication,
} from "modules";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Icon, useToast } from "@chakra-ui/react";
import { useChartTrackingContext, usePermissions } from "contexts";

import { ChartForm } from "modules/charts-shared-v2";
import { EmrClinicalImpression } from "@medstonetech/slate-icons";
import { FormPromptWithSaveProps } from "shared/prompt";
import { Loading } from "shared";
import { extractApiErrorMessage } from "utils";
import { formMessages } from "messages";
import { useParams } from "react-router-dom";

const sectionId = "SCLI";
const chartCode = "C016x";

function T16ClinicalImpression() {
  const { encounterId = "" } = useParams<ChartRouteBaseParams>();
  const useFormContext = useForm<ChartFormType>();
  const {
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = useFormContext;

  const { scope } = usePermissions();
  const isReadOnly = !scope("provider:ci").isEditable;

  const {
    data,
    isLoading: getIsLoading,
    error: getError,
  } = useChartApplication({
    chartCode,
    encounterId,
    sectionId,
  });
  const { mutateAsync: updateChartApplication, isLoading: updateIsLoading } =
    useUpdateChartApplication({
      chartCode,
      encounterId,
      sectionId,
    });
  const toast = useToast();
  const formObject = getValues();

  const { trackChartInteractions, isLoading: isTrackingLoading } =
    useChartTrackingContext();

  const onSubmit: SubmitHandler<ChartFormType> = React.useCallback(
    async (values) => {
      try {
        const payload = mapFormToPayload(values);
        await updateChartApplication({
          input: payload,
          chartFavoriteId: getValues("chartFavoriteId") as unknown as string,
        });

        await trackChartInteractions(
          data?.data.map((x) => ({ code: x.code, answers: x.answers })) || [],
          payload,
          {
            encounterId,
            chartCode,
            sectionId,
            chartType: "application",
          }
        );

        // Reset form with latest values to set isDirty to false
        reset(values);
        toast({
          description: formMessages.updateSuccess("Clinical Impression"),
        });
      } catch (error) {
        toast({ description: extractApiErrorMessage(error) });
      }
    },
    [
      updateChartApplication,
      trackChartInteractions,
      data?.data,
      encounterId,
      reset,
      toast,
      getValues,
    ]
  );

  const handleSaveAndNext = useSaveAndNext(
    handleSubmit(onSubmit),
    "clinical-impression",
    "provider",
    isDirty
  );

  const onRouteChangeSave: FormPromptWithSaveProps["onSave"] = async (
    onComplete
  ) => {
    try {
      const payload = mapFormToPayload(formObject);
      await updateChartApplication({
        input: payload,
        chartFavoriteId: getValues("chartFavoriteId") as unknown as string,
      });
      await trackChartInteractions(
        data?.data.map((x) => ({ code: x.code, answers: x.answers })) || [],
        payload,
        {
          encounterId,
          chartCode,
          sectionId,
          chartType: "application",
        }
      );
      toast({ description: formMessages.updateSuccess("Clinical Impression") });
      onComplete();
    } catch (error) {
      toast({ description: extractApiErrorMessage(error) });
    }
  };

  React.useEffect(() => {
    if (data) {
      const form = mapPayloadToFormObject(data.data);
      reset(form);
    }
  }, [data, reset]);

  React.useEffect(() => {
    if (getError) {
      toast({ description: extractApiErrorMessage(getError) });
    }
  }, [getError, toast]);

  if (getIsLoading) {
    return <Loading />;
  }

  const chartFavoriteEditMode: Record<
    "imported",
    ChartRowElementImportedProps
  > | null = getValues("chartFavoriteEditMode")
    ? {
        imported: {
          type: "nursing",
        },
      }
    : null;

  return (
    <FormProvider {...useFormContext}>
      <ChartForm
        onSubmit={handleSubmit(onSubmit)}
        onRouteChangeSave={onRouteChangeSave}
        isDirty={isDirty}
        isPromptSaveLoading={updateIsLoading || isTrackingLoading}
        disabled={isReadOnly}
        sectionId={sectionId}
      >
        <ChartSectionHeader
          icon={<Icon as={EmrClinicalImpression} />}
          chartId={chartCode}
          encounterId={encounterId}
          sectionId={sectionId}
          showActionsBtns
          isLoading={updateIsLoading}
          onSaveAndNext={handleSaveAndNext}
          showFavoritesBtn={!isReadOnly}
        >
          Clinical Impression {isReadOnly ? "(Read Only)" : ""}
        </ChartSectionHeader>
        <ChartSubsection
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Initial visit unless marked:"
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q001.${chartCode}${sectionId}Q001A001`}
                />
                <ChartRowElement
                  label="subsequent"
                  name={`Q001.${chartCode}${sectionId}Q001A002`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="sequelae"
                  name={`Q001.${chartCode}${sectionId}Q001A003`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement type="empty" />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q002"
              subsectionLabel="Skin"
            >
              Skin
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Laceration:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A001`}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A002`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Abrasion:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A003`}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A004`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Contusion:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A005`}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A006`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Burn:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A005`}
                />
                <ChartRowElement
                  label="thermal"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A006`}
                />
                <ChartRowElement
                  label="electrical"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A007`}
                />
                <ChartRowElement
                  label="chemical:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A008`}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A009`}
                  type="input"
                  {...chartFavoriteEditMode}
                  width="300px"
                />
                <ChartRowElement
                  label="accident"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A010`}
                />
                <ChartRowElement
                  label="self-harm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A011`}
                />
                <ChartRowElement
                  label="assault"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A012`}
                />
                <ChartRowElement
                  label="unknown"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q002.${chartCode}${sectionId}Q002A013`}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A014`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="1st"
                  name={`Q002.${chartCode}${sectionId}Q002A015`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="area:"
                  name={`Q002.${chartCode}${sectionId}Q002A016`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  textDecoration="underline"
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A017`}
                  type="input"
                  {...chartFavoriteEditMode}
                  width="600px"
                />
                <ChartRowElement
                  label=";"
                  name={`Q002.${chartCode}${sectionId}Q002A018`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A019`}
                  type="input"
                  {...chartFavoriteEditMode}
                  width="50px"
                />
                <ChartRowElement
                  label="%"
                  name={`Q002.${chartCode}${sectionId}Q002A020`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A021`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="2nd"
                  name={`Q002.${chartCode}${sectionId}Q002A022`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="area:"
                  name={`Q002.${chartCode}${sectionId}Q002A023`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  textDecoration="underline"
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A024`}
                  type="input"
                  {...chartFavoriteEditMode}
                  width="600px"
                />
                <ChartRowElement
                  label=";"
                  name={`Q002.${chartCode}${sectionId}Q002A025`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A026`}
                  type="input"
                  {...chartFavoriteEditMode}
                  width="50px"
                />
                <ChartRowElement
                  label="%"
                  name={`Q002.${chartCode}${sectionId}Q002A027`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A028`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="3rd"
                  name={`Q002.${chartCode}${sectionId}Q002A029`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="area:"
                  name={`Q002.${chartCode}${sectionId}Q002A030`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  textDecoration="underline"
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A031`}
                  type="input"
                  {...chartFavoriteEditMode}
                  width="600px"
                />
                <ChartRowElement
                  label=";"
                  name={`Q002.${chartCode}${sectionId}Q002A032`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A033`}
                  type="input"
                  {...chartFavoriteEditMode}
                  width="50px"
                />
                <ChartRowElement
                  label="%"
                  name={`Q002.${chartCode}${sectionId}Q002A034`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A035`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Total BSA="
                  fontStyle="italic"
                  name={`Q002.${chartCode}${sectionId}Q002A036`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A037`}
                  type="input"
                  {...chartFavoriteEditMode}
                  width="100px"
                />
                <ChartRowElement
                  label="%"
                  name={`Q002.${chartCode}${sectionId}Q002A038`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="other"
                  name={`Q002.${chartCode}${sectionId}Q002A039`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q002.${chartCode}${sectionId}Q002A040`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q003"
              subsectionLabel="Strain"
            >
              Strain
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="L"
                  name={`Q003.${chartCode}${sectionId}Q003A001`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="R"
                  name={`Q003.${chartCode}${sectionId}Q003A002`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A003`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="other"
                  name={`Q003.${chartCode}${sectionId}Q003A004`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q003.${chartCode}${sectionId}Q003A005`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q004"
              subsectionLabel="Sprain"
            >
              Sprain
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Spine:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q004.${chartCode}${sectionId}Q004A001`}
                />
                <ChartRowElement
                  label="C"
                  name={`Q004.${chartCode}${sectionId}Q004A002`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="T"
                  name={`Q004.${chartCode}${sectionId}Q004A003`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="L"
                  name={`Q004.${chartCode}${sectionId}Q004A004`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q004.${chartCode}${sectionId}Q004A005`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="other"
                  name={`Q004.${chartCode}${sectionId}Q004A006`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q004.${chartCode}${sectionId}Q004A007`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q005"
              subsectionLabel="Ortho"
            >
              Ortho
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Fracture:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q005.${chartCode}${sectionId}Q005A001`}
                />
                <ChartRowElement
                  label="closed"
                  name={`Q005.${chartCode}${sectionId}Q005A002`}
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  label="open"
                  name={`Q005.${chartCode}${sectionId}Q005A003`}
                  fontStyle="italic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q005.${chartCode}${sectionId}Q005A004`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Dislocation"
                  name={`Q005.${chartCode}${sectionId}Q005A005`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q005.${chartCode}${sectionId}Q005A006`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="other"
                  name={`Q005.${chartCode}${sectionId}Q005A007`}
                  type="cross-option"
                  {...chartFavoriteEditMode}
                />
                <ChartRowElement
                  name={`Q005.${chartCode}${sectionId}Q005A008`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q006"
              subsectionLabel="CV / Resp"
            >
              CV / Resp
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Cardiac arrest"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A001`}
                />
                <ChartRowElement
                  name={`Q006.${chartCode}${sectionId}Q006A002`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Cardiac contusion"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A003`}
                />
                <ChartRowElement
                  name={`Q006.${chartCode}${sectionId}Q006A004`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Hemopericardium"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A005`}
                />
                <ChartRowElement
                  name={`Q006.${chartCode}${sectionId}Q006A006`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Hemothorax"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A007`}
                />
                <ChartRowElement
                  label="L"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A008`}
                />
                <ChartRowElement
                  label="R"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A009`}
                />
                <ChartRowElement
                  name={`Q006.${chartCode}${sectionId}Q006A010`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Pneumothorax"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A011`}
                />
                <ChartRowElement
                  label="L"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A012`}
                />
                <ChartRowElement
                  label="R"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A013`}
                />
                <ChartRowElement
                  label="tension"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q006.${chartCode}${sectionId}Q006A014`}
                />
                <ChartRowElement
                  name={`Q006.${chartCode}${sectionId}Q006A015`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Pulmorany"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A016`}
                />
                <ChartRowElement
                  label="L"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A017`}
                />
                <ChartRowElement
                  label="R"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A018`}
                />
                <ChartRowElement
                  label="contusion"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q006.${chartCode}${sectionId}Q006A019`}
                />
                <ChartRowElement
                  label="lac"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q006.${chartCode}${sectionId}Q006A020`}
                />
                <ChartRowElement
                  name={`Q006.${chartCode}${sectionId}Q006A021`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Pneumomediastinum"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A022`}
                />
                <ChartRowElement
                  name={`Q006.${chartCode}${sectionId}Q006A023`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="other"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q006.${chartCode}${sectionId}Q006A024`}
                />
                <ChartRowElement
                  name={`Q006.${chartCode}${sectionId}Q006A025`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q007"
              subsectionLabel="Abdomen / GI / GU"
            >
              Abdomen / GI / GU
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Hemoperitoneum"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A001`}
                />
                <ChartRowElement
                  name={`Q007.${chartCode}${sectionId}Q007A002`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Liver:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A003`}
                />
                <ChartRowElement
                  label="contusion"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A004`}
                />
                <ChartRowElement
                  label="lac:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A005`}
                />
                <ChartRowElement
                  name={`Q007.${chartCode}${sectionId}Q007A006`}
                  type="input"
                  {...chartFavoriteEditMode}
                  width="50px"
                />
                <ChartRowElement
                  label="cm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A007`}
                />
                <ChartRowElement
                  label="stellate"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A008`}
                />
                <ChartRowElement
                  label="multiple"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A009`}
                />
                <ChartRowElement
                  name={`Q007.${chartCode}${sectionId}Q007A010`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Spleen:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A011`}
                />
                <ChartRowElement
                  label="contusion"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A012`}
                />
                <ChartRowElement
                  label="<2cm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A013`}
                />
                <ChartRowElement
                  label=">2cm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A014`}
                />
                <ChartRowElement
                  label="lac"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A015`}
                />
                <ChartRowElement
                  label="<1cm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A016`}
                />
                <ChartRowElement
                  label="1-3cm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A017`}
                />
                <ChartRowElement
                  label=">3cm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A018`}
                />
                <ChartRowElement
                  name={`Q007.${chartCode}${sectionId}Q007A019`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Stomach Contusion"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A020`}
                />
                <ChartRowElement
                  name={`Q007.${chartCode}${sectionId}Q007A021`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Small Bowel Contusion:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A022`}
                />
                <ChartRowElement
                  label="deodenum"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A023`}
                />
                <ChartRowElement
                  label="jejunum"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A024`}
                />
                <ChartRowElement
                  label="ileum"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A025`}
                />
                <ChartRowElement
                  name={`Q007.${chartCode}${sectionId}Q007A026`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Kidney:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A027`}
                />
                <ChartRowElement
                  label="L"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A028`}
                />
                <ChartRowElement
                  label="R"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A029`}
                />
                <ChartRowElement
                  label="contusion"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A030`}
                />
                <ChartRowElement
                  label="<2cm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A031`}
                />
                <ChartRowElement
                  label=">2cm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A032`}
                />
                <ChartRowElement
                  label=">lac:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A033`}
                />
                <ChartRowElement
                  label="<1cm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A034`}
                />
                <ChartRowElement
                  label="1-3cm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A035`}
                />
                <ChartRowElement
                  label=">3cm"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q007.${chartCode}${sectionId}Q007A036`}
                />
                <ChartRowElement
                  name={`Q007.${chartCode}${sectionId}Q007A037`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="other"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q007.${chartCode}${sectionId}Q007A038`}
                />
                <ChartRowElement
                  name={`Q007.${chartCode}${sectionId}Q007A039`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q008"
              subsectionLabel="Neuro"
            >
              Neuro
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Concussion:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A001`}
                />
                <ChartRowElement
                  label="lost consciousness:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A002`}
                />
                <ChartRowElement
                  label="yes"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q008.${chartCode}${sectionId}Q008A003`}
                />
                <ChartRowElement
                  label="no"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q008.${chartCode}${sectionId}Q008A004`}
                />
                <ChartRowElement
                  label="unknown"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q008.${chartCode}${sectionId}Q008A005`}
                />
                <ChartRowElement
                  label="duration:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q008.${chartCode}${sectionId}Q008A006`}
                />
                <ChartRowElement
                  name={`Q008.${chartCode}${sectionId}Q008A007`}
                  type="input"
                  {...chartFavoriteEditMode}
                  width="50px"
                />
                <ChartRowElement
                  label="seconds"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q008.${chartCode}${sectionId}Q008A008`}
                />
                <ChartRowElement
                  label="minutes"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q008.${chartCode}${sectionId}Q008A009`}
                />
                <ChartRowElement
                  label="hours"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q008.${chartCode}${sectionId}Q008A010`}
                />
                <ChartRowElement
                  name={`Q008.${chartCode}${sectionId}Q008A011`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Diffuse traumatic brain injury"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A012`}
                />
                <ChartRowElement
                  name={`Q008.${chartCode}${sectionId}Q008A013`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Shaken baby syndrome"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A014`}
                />
                <ChartRowElement
                  name={`Q008.${chartCode}${sectionId}Q008A015`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Cerebral:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A016`}
                />
                <ChartRowElement
                  label="L"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A017`}
                />
                <ChartRowElement
                  label="R"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A018`}
                />
                <ChartRowElement
                  label="contusion"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q008.${chartCode}${sectionId}Q008A019`}
                />
                <ChartRowElement
                  label="lac"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q008.${chartCode}${sectionId}Q008A020`}
                />
                <ChartRowElement
                  label="traumatic hemorrhage"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q008.${chartCode}${sectionId}Q008A021`}
                />
                <ChartRowElement
                  name={`Q008.${chartCode}${sectionId}Q008A022`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Epidural"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A023`}
                />
                <ChartRowElement
                  label="Subdural"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A024`}
                />
                <ChartRowElement
                  label="Traumatic SAH"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A025`}
                />
                <ChartRowElement
                  label="L"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A026`}
                />
                <ChartRowElement
                  label="R"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A027`}
                />
                <ChartRowElement
                  name={`Q008.${chartCode}${sectionId}Q008A028`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Seizure"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A029`}
                />
                <ChartRowElement
                  name={`Q008.${chartCode}${sectionId}Q008A030`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="other"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q008.${chartCode}${sectionId}Q008A031`}
                />
                <ChartRowElement
                  name={`Q008.${chartCode}${sectionId}Q008A032`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q009"
              subsectionLabel="Other"
            >
              Other
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  label="Child abuse:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q009.${chartCode}${sectionId}Q009A001`}
                />
                <ChartRowElement
                  label="physical"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q009.${chartCode}${sectionId}Q009A002`}
                />
                <ChartRowElement
                  label="sexual"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q009.${chartCode}${sectionId}Q009A003`}
                />
                <ChartRowElement
                  label="reported"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q009.${chartCode}${sectionId}Q009A004`}
                />
                <ChartRowElement
                  label="suspected"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q009.${chartCode}${sectionId}Q009A005`}
                />
                <ChartRowElement
                  name={`Q009.${chartCode}${sectionId}Q009A006`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Diaphragm rupture"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q009.${chartCode}${sectionId}Q009A007`}
                />
                <ChartRowElement
                  name={`Q009.${chartCode}${sectionId}Q009A008`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Hypokalemia"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q009.${chartCode}${sectionId}Q009A009`}
                />
                <ChartRowElement
                  name={`Q009.${chartCode}${sectionId}Q009A010`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Leukocytosis"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle=""
                  name={`Q009.${chartCode}${sectionId}Q009A011`}
                />
                <ChartRowElement
                  name={`Q009.${chartCode}${sectionId}Q009A012`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Shock"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q009.${chartCode}${sectionId}Q009A013`}
                />
                <ChartRowElement
                  label="traumatic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q009.${chartCode}${sectionId}Q009A014`}
                />
                <ChartRowElement
                  label="hypovolemic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q009.${chartCode}${sectionId}Q009A015`}
                />
                <ChartRowElement
                  name={`Q009.${chartCode}${sectionId}Q009A016`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Penetrating injury to abd cavity"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q009.${chartCode}${sectionId}Q009A017`}
                />
                <ChartRowElement
                  name={`Q009.${chartCode}${sectionId}Q009A018`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Tooth #:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q009.${chartCode}${sectionId}Q009A017`}
                />
                <ChartRowElement
                  name={`Q009.${chartCode}${sectionId}Q009A018`}
                  type="input"
                  {...chartFavoriteEditMode}
                  width="50px"
                />
                <ChartRowElement
                  label="fx"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q009.${chartCode}${sectionId}Q009A019`}
                />
                <ChartRowElement
                  label="avulsion"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q009.${chartCode}${sectionId}Q009A020`}
                />
                <ChartRowElement
                  label="sublux"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q009.${chartCode}${sectionId}Q009A021`}
                />
                <ChartRowElement
                  name={`Q009.${chartCode}${sectionId}Q009A022`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="Acute pain:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q009.${chartCode}${sectionId}Q009A023`}
                />
                <ChartRowElement
                  label="traumatic"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q009.${chartCode}${sectionId}Q009A024`}
                />
                <ChartRowElement
                  label="to:"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  fontStyle="italic"
                  name={`Q009.${chartCode}${sectionId}Q009A025`}
                />
                <ChartRowElement
                  name={`Q009.${chartCode}${sectionId}Q009A026`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="other"
                  type="cross-option"
                  {...chartFavoriteEditMode}
                  name={`Q009.${chartCode}${sectionId}Q009A027`}
                />
                <ChartRowElement
                  name={`Q009.${chartCode}${sectionId}Q009A028`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
      </ChartForm>
    </FormProvider>
  );
}

export { T16ClinicalImpression };
